import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PlotWaveform, WavePoint } from './respiratoryMuscleDomain';

export const WAVEFORM_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f97316', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

export const WaveformChart = ({
  title,
  color,
  unit,
  dataKey,
  data,
}: {
  title: string;
  color: string;
  unit: string;
  dataKey: keyof WavePoint;
  data: WavePoint[];
}) => (
  <div className="rounded-md border bg-white p-3 shadow-sm">
    <div className="mb-2 text-sm font-semibold text-slate-800">{title}</div>
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="time" label={{ value: 'Tempo (s)', position: 'insideBottomRight', offset: -5 }} />
        <YAxis />
        <Tooltip
          formatter={(value: unknown) => (typeof value === 'number' ? `${value.toFixed(2)} ${unit}` : value)}
          labelFormatter={(label: unknown) => `t = ${label}s`}
        />
        <Line type="monotone" dataKey={dataKey} stroke={color} dot={false} strokeWidth={2} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export const MultiSeriesComparisonChart = ({
  title,
  unit,
  data,
  seriesMeta,
}: {
  title: string;
  unit: string;
  data: Array<Record<string, number>>;
  seriesMeta: { key: string; label: string; color: string }[];
}) => (
  <div className="rounded-md border bg-white p-3 shadow-sm">
    <div className="mb-2 text-sm font-semibold text-slate-800">{title}</div>
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="percent"
          label={{ value: 'Tempo normalizado (%)', position: 'insideBottomRight', offset: -5 }}
          tickFormatter={(value) => `${value}`}
        />
        <YAxis />
        <Tooltip
          formatter={(value: unknown) => (typeof value === 'number' ? `${value.toFixed(2)} ${unit}` : value)}
          labelFormatter={(label: unknown) => `${label}% do tempo`}
        />
        <Legend />
        {seriesMeta.map((series) => (
          <Line
            key={series.key}
            type="monotone"
            dataKey={series.key}
            name={series.label}
            stroke={series.color}
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export const OverlappedPressureChart = ({
  data,
  breaths,
  height = 260,
}: {
  data: Array<Record<string, number>>;
  breaths: PlotWaveform[];
  height?: number;
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis dataKey="index" tick={false} label={{ value: 'Tempo (amostras)', position: 'insideBottom', offset: -5 }} />
      <YAxis label={{ value: 'Pressao (cmH2O)', angle: -90, position: 'insideLeft' }} />
      <Tooltip formatter={(value: unknown) => (typeof value === 'number' ? `${value.toFixed(2)} cmH2O` : value)} />
      {breaths.map((breath, index) => (
        <Line
          key={breath.breathNumber}
          type="monotone"
          dataKey={`breath_${breath.breathNumber}`}
          stroke={WAVEFORM_COLORS[index % WAVEFORM_COLORS.length]}
          strokeWidth={1.5}
          dot={false}
          name={`Resp ${breath.breathNumber}`}
          isAnimationActive={false}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);
